/**
 * Frontend mirror of backend garage cluster catalog (Phase 1).
 * Keep in sync with sparepe_backend/src/constants/garageServiceClusters.js
 */

export const GARAGE_TIERS = Object.freeze([
  'small',
  'medium',
  'large',
  'custom_workshop',
  'transformer',
]);

export const GARAGE_TIER_ALIASES = Object.freeze({
  small_garage: 'small',
  medium_garage: 'medium',
  large_garage: 'large',
  custom_garage: 'custom_workshop',
  transformal: 'transformer',
  small: 'small',
  medium: 'medium',
  large: 'large',
  enterprise: 'large',
  custom_workshop: 'custom_workshop',
  transformer: 'transformer',
});

export const TIER_RANK = Object.freeze({
  small: 1,
  medium: 2,
  large: 3,
});

export const CLUSTERS = Object.freeze([
  { id: 1, key: 'general_periodic', label: 'General & Periodic Service', tierFloor: 'small', fuelGate: 'all' },
  { id: 2, key: 'battery_electrical_lights', label: 'Battery, Electrical & Lights', tierFloor: 'small', fuelGate: 'all' },
  { id: 3, key: 'tyre_wheel', label: 'Tyre & Wheel', tierFloor: 'small', fuelGate: 'all' },
  { id: 4, key: 'brake_suspension', label: 'Brake & Suspension', tierFloor: 'small', fuelGate: 'all' },
  { id: 5, key: 'ac_climate', label: 'AC & Climate', tierFloor: 'small', fuelGate: 'all' },
  { id: 6, key: 'engine_fuel', label: 'Engine & Fuel System', tierFloor: 'small', fuelGate: 'all' },
  { id: 7, key: 'clutch_transmission', label: 'Clutch & Transmission', tierFloor: 'medium', fuelGate: 'all' },
  { id: 8, key: 'body_paint_glass', label: 'Body, Paint & Glass', tierFloor: 'medium', fuelGate: 'all' },
  { id: 9, key: 'diagnostics_emission_insurance', label: 'Diagnostics, Emission & Insurance', tierFloor: 'small', fuelGate: 'all' },
  {
    id: 10,
    key: 'fuel_specialist',
    label: 'Fuel-Specialist (CNG/LPG/EV)',
    tierFloor: 'small',
    fuelGate: 'cng_lpg_only',
    fuelSubTypes: ['cng_lpg', 'ev_hybrid'],
  },
]);

export const CLUSTER_PAIRING_NUDGES = Object.freeze([
  { from: 5, to: 2, message: '68% of AC customers also need Electrical — add Battery, Electrical & Lights?' },
  { from: 4, to: 3, message: 'Brake jobs often need alignment — add Tyre & Wheel?' },
]);

export const MIN_CLUSTERS_SMALL = 2;

export const GARAGE_CAPABILITY_TAGS = Object.freeze([
  { key: 'ecu_remap', label: 'ECU Remapping / Chip Tuning' },
  { key: 'performance_exhaust', label: 'Performance Exhaust Systems' },
  { key: 'forced_induction', label: 'Air Intake & Forced Induction' },
  { key: 'suspension_tune', label: 'Suspension & Chassis Tuning' },
  { key: 'brake_upgrade', label: 'Brake Upgrade' },
  { key: 'engine_build', label: 'Engine Build' },
  { key: 'drivetrain', label: 'Transmission & Drivetrain' },
  { key: 'aero_aesthetic', label: 'Aesthetic & Aero' },
  { key: 'dyno', label: 'Dyno Testing & Diagnostics' },
]);

export const GARAGE_PROJECT_TAGS = Object.freeze([
  { key: 'restomod', label: 'Classic Car Restoration & Restomod' },
  { key: 'overland', label: 'Off-Road / Overland Build' },
  { key: 'performance_street', label: 'Performance Street Build' },
  { key: 'vip_luxury', label: 'VIP / Luxury Interior Conversion' },
  { key: 'commercial_conversion', label: 'Commercial Vehicle Customization' },
  { key: 'ev_conversion', label: 'EV Conversion (ICE to electric)' },
]);

export const FULFILLMENT_FLAG_OPTIONS = Object.freeze([
  { key: 'doorstep', label: 'Doorstep / Mobile Mechanic' },
  { key: 'pickupDrop', label: 'Pickup & Drop' },
  { key: 'roadsideAssistance', label: 'Roadside Assistance' },
  { key: 'sameDayTurnaround', label: 'Same-day Turnaround' },
]);

export function normalizeGarageTier(raw) {
  const key = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (!key) return '';
  return GARAGE_TIER_ALIASES[key] || (GARAGE_TIERS.includes(key) ? key : '');
}

export function tierMeetsFloor(garageTier, tierFloor) {
  const g = TIER_RANK[normalizeGarageTier(garageTier)] || 0;
  const f = TIER_RANK[normalizeGarageTier(tierFloor)] || 0;
  return g >= f && g > 0;
}

export function getClusterById(clusterId) {
  const id = Number(clusterId);
  return CLUSTERS.find((c) => c.id === id) || null;
}

export function getDefaultClustersForTier(tier) {
  if (normalizeGarageTier(tier) === 'large') {
    return CLUSTERS.map((c) => c.id);
  }
  return [];
}

export function getBookingModeForTier(tier) {
  const canonical = normalizeGarageTier(tier);
  if (canonical === 'custom_workshop') return 'quote';
  if (canonical === 'transformer') return 'staged_quote';
  return 'instant';
}

export function getClustersAvailableForTier(tier) {
  const canonical = normalizeGarageTier(tier);
  if (!canonical || canonical === 'custom_workshop' || canonical === 'transformer') {
    return [];
  }
  return CLUSTERS.filter((c) => tierMeetsFloor(canonical, c.tierFloor));
}

export function validateClusterSelection(tier, clusterIds, fuelSpecialistTypes = []) {
  const errors = [];
  const canonical = normalizeGarageTier(tier);
  const ids = (Array.isArray(clusterIds) ? clusterIds : []).map(Number).filter((n) => n >= 1 && n <= 10);

  if (canonical === 'small' && ids.length < MIN_CLUSTERS_SMALL) {
    errors.push(`Select at least ${MIN_CLUSTERS_SMALL} service clusters.`);
  }

  for (const id of ids) {
    const cluster = getClusterById(id);
    if (!cluster) continue;
    if (!tierMeetsFloor(canonical, cluster.tierFloor)) {
      errors.push(`"${cluster.label}" requires ${cluster.tierFloor} tier or above.`);
    }
    if (id === 10 && fuelSpecialistTypes.includes('ev_hybrid') && canonical !== 'large') {
      errors.push('EV/Hybrid requires Large garage tier.');
    }
  }

  return errors;
}

const FUEL_CLUSTER_ID = 10;

/**
 * Small/Medium garages only support CNG/LPG for fuel-specialist cluster.
 * Auto-default when cluster 10 is selected; Large garages must pick explicitly.
 */
export function resolveFuelSpecialistTypes(tier, clusterIds, fuelSpecialistTypes = []) {
  const canonical = normalizeGarageTier(tier);
  const ids = (Array.isArray(clusterIds) ? clusterIds : []).map(Number).filter((n) => n >= 1 && n <= 10);
  if (!ids.includes(FUEL_CLUSTER_ID)) return [];

  let types = (Array.isArray(fuelSpecialistTypes) ? fuelSpecialistTypes : [])
    .filter((t) => t === 'cng_lpg' || t === 'ev_hybrid');

  if (canonical !== 'large') {
    types = types.filter((t) => t !== 'ev_hybrid');
    if (!types.length) types = ['cng_lpg'];
  }

  return types;
}

export function fuelSpecialistSelectionRequired(tier, clusterIds) {
  const canonical = normalizeGarageTier(tier);
  const ids = (Array.isArray(clusterIds) ? clusterIds : []).map(Number);
  return canonical === 'large' && ids.includes(FUEL_CLUSTER_ID);
}
